import { archivedTwinLogicQueryAuthoritySchema } from "./twin-logic-execution-authority.js";
import { z } from "zod";
import { canonicalResultValueSchema } from "./canonical-result.js";
const sha256Schema = z.string().regex(/^[a-f0-9]{64}$/u);
const evidenceCutIdSchema = z.string().regex(/^ecut_[a-f0-9]{64}$/u);
export const twinExecutionFreshnessPolicySchema = z.object({
    kind: z.literal("live_warehouse_query_snapshot"),
    reuse: z.literal("same_run_only"),
}).strict();
export const twinExecutionCoverageAuthoritySchema = z.object({
    coverageItemId: z.string().trim().min(1).max(160),
    coverageKey: z.string().trim().min(3).max(160),
    publicationVersionId: z.string().trim().min(1).max(200),
    productSlug: z.string().trim().min(1).max(120),
    productVersion: z.number().int().positive(),
    contractHash: sha256Schema,
    relation: z.string().trim().min(1).max(260),
}).strict();
export const twinExecutionProductAuthoritySchema = z.object({
    publicationVersionId: z.string().trim().min(1).max(200),
    productSlug: z.string().trim().min(1).max(120),
    productVersion: z.number().int().positive(),
    relation: z.string().trim().min(1).max(260),
    twinLogicAuthority: archivedTwinLogicQueryAuthoritySchema.optional(),
}).strict();
export const twinExecutionArtifactClosureSchema = z.object({
    artifactId: z.string().trim().min(1).max(200),
    versionId: z.string().trim().min(1).max(200),
    contentSha256: sha256Schema,
    byteLength: z.number().int().nonnegative(),
    mimeType: z.literal("text/csv"),
    filename: z.string().trim().min(1).max(240),
    rowCount: z.number().int().nonnegative(),
    columnCount: z.number().int().nonnegative().max(256),
    columns: z.array(z.string().trim().min(1).max(240)).max(256),
    complete: z.boolean(),
}).strict().superRefine((artifact, context) => {
    if (artifact.columnCount !== artifact.columns.length) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["columnCount"],
            message: "Artifact column count must match its declared columns.",
        });
    }
    if (new Set(artifact.columns).size !== artifact.columns.length) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["columns"],
            message: "Artifact columns must be unique.",
        });
    }
});
const compactFactSchema = z.object({
    label: z.string().trim().min(1).max(240),
    values: z.record(z.string().trim().min(1).max(240), canonicalResultValueSchema),
}).strict().superRefine((fact, context) => {
    if (Object.keys(fact.values).length > 24) {
        context.addIssue({ code: z.ZodIssueCode.custom, path: ["values"],
            message: "A compact fact may contain at most 24 typed values." });
    }
});
export const certifiedCompactFactEnvelopeSchema = z.object({
    schemaVersion: z.literal("mlx.certified-compact-facts.v1"),
    status: z.enum(["present", "absent", "inconclusive"]),
    rowCount: z.number().int().nonnegative(),
    columnCount: z.number().int().nonnegative().max(256),
    complete: z.boolean(),
    zeroRowAssertion: z.boolean(),
    sourceRelations: z.array(z.string().trim().min(1).max(300)).min(1).max(16),
    grain: z.object({
        kind: z.literal("query_result_row"),
        description: z.string().trim().min(1).max(320),
    }).strict(),
    timeBoundary: z.object({
        kind: z.enum(["observed_values", "not_expressed"]),
        start: z.string().trim().min(1).max(40).optional(),
        end: z.string().trim().min(1).max(40).optional(),
        columns: z.array(z.string().trim().min(1).max(240)).max(8),
    }).strict(),
    currencyCodes: z.array(z.string().regex(/^[A-Z]{3}$/u)).max(8),
    units: z.array(z.object({
        column: z.string().trim().min(1).max(240),
        unit: z.enum(["currency_code", "percent", "count", "days", "hours", "date", "timestamp", "boolean"]),
    }).strict()).max(24),
    facts: z.array(compactFactSchema).max(12),
    caveats: z.array(z.string().trim().min(1).max(500)).max(12),
}).strict().superRefine((envelope, context) => {
    const expectedStatus = envelope.rowCount > 0
        ? "present"
        : envelope.complete ? "absent" : "inconclusive";
    if (envelope.zeroRowAssertion !== (envelope.rowCount === 0 && envelope.complete) ||
        envelope.status !== expectedStatus) {
        context.addIssue({ code: z.ZodIssueCode.custom, path: ["zeroRowAssertion"],
            message: "Status and zero-row assertion must agree with row count." });
    }
    if (envelope.rowCount === 0 && envelope.facts.length > 0) {
        context.addIssue({ code: z.ZodIssueCode.custom, path: ["facts"],
            message: "A certified zero-row result cannot contain facts." });
    }
    if (new Set(envelope.sourceRelations).size !== envelope.sourceRelations.length ||
        new Set(envelope.currencyCodes).size !== envelope.currencyCodes.length) {
        context.addIssue({ code: z.ZodIssueCode.custom, path: [],
            message: "Compact envelope relations and currencies must be unique." });
    }
    if (JSON.stringify(envelope).length > 32_000) {
        context.addIssue({ code: z.ZodIssueCode.custom, path: [],
            message: "Compact fact envelope exceeds 32,000 characters." });
    }
});
export function certifiedCompactFactEnvelopeJson(input) {
    const value = certifiedCompactFactEnvelopeSchema.parse(input);
    return JSON.stringify(value);
}
export const TWIN_EXECUTION_CERTIFICATE_SCHEMA_VERSION = "mlx.execution-certificate.v0";
const certificateClosureShape = {
    schemaVersion: z.literal(TWIN_EXECUTION_CERTIFICATE_SCHEMA_VERSION),
    tenantSlug: z.string().trim().min(1).max(120),
    organisationId: z.string().trim().min(1).max(160),
    runId: z.string().trim().min(1).max(200),
    toolCallId: z.string().trim().min(1).max(200),
    sqlSha256: sha256Schema,
    queriedTables: z.array(z.string().trim().min(1).max(300)).min(1).max(256),
    engineVersion: z.string().trim().min(1).max(200),
    organisationScope: z.string().trim().min(1).max(160),
    warehouseOrganisationId: z.string().trim().min(1).max(200).optional(),
    canonicalizationVersion: z.enum([
        "mlx.canonical-result.v1",
        "mlx.csv-artifact-bytes.v1",
    ]),
    canonicalResultSha256: sha256Schema,
    observedCutId: evidenceCutIdSchema.nullable(),
    observedAt: z.number().int().positive().optional(),
    freshnessPolicy: twinExecutionFreshnessPolicySchema.optional(),
    queryPlanSha256: sha256Schema.optional(),
    coverageAuthority: twinExecutionCoverageAuthoritySchema.optional(),
    productAuthority: twinExecutionProductAuthoritySchema.optional(),
    /** The second product a governed join read; its dimensions only, along a declared key. */
    joinedProductAuthority: twinExecutionProductAuthoritySchema.optional(),
    artifactClosure: twinExecutionArtifactClosureSchema.optional(),
    compactFactEnvelope: certifiedCompactFactEnvelopeSchema.optional(),
    compactFactEnvelopeSha256: sha256Schema.optional(),
};
function refineCertificateClosure(certificate, context) {
    if (certificate.organisationScope !== certificate.organisationId) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["organisationScope"],
            message: "Certificate organisation scope must equal its organisation id.",
        });
    }
    if (new Set(certificate.queriedTables).size !== certificate.queriedTables.length) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["queriedTables"],
            message: "Certificate queried tables must be unique.",
        });
    }
    if (certificate.coverageAuthority &&
        !certificate.queriedTables.includes(certificate.coverageAuthority.relation)) {
        context.addIssue({ code: z.ZodIssueCode.custom, path: ["coverageAuthority", "relation"],
            message: "Coverage authority relation must be one of the queried tables." });
    }
    if (certificate.productAuthority &&
        !certificate.queriedTables.includes(certificate.productAuthority.relation)) {
        context.addIssue({ code: z.ZodIssueCode.custom, path: ["productAuthority", "relation"],
            message: "Product authority relation must be one of the queried tables." });
    }
    const joined = certificate.joinedProductAuthority;
    if (joined) {
        // A joined result reads exactly its two products: the queried one and the one it joins.
        const relations = new Set([certificate.productAuthority?.relation, joined.relation]);
        if (!certificate.productAuthority || relations.size !== 2 ||
            joined.productSlug === certificate.productAuthority.productSlug ||
            certificate.queriedTables.some((relation) => !relations.has(relation)) ||
            !certificate.queriedTables.includes(joined.relation)) {
            context.addIssue({ code: z.ZodIssueCode.custom, path: ["joinedProductAuthority"],
                message: "A joined product authority needs a different queried product, and the certificate may read only those two relations." });
        }
        if (joined.twinLogicAuthority &&
            certificate.warehouseOrganisationId !== joined.twinLogicAuthority.warehouseOrganisationId) {
            context.addIssue({ code: z.ZodIssueCode.custom, path: ["joinedProductAuthority", "twinLogicAuthority"],
                message: "Twin Logic certificates must bind the exact admitted warehouse organisation." });
        }
    }
    const twinLogicAuthority = certificate.productAuthority?.twinLogicAuthority;
    if (twinLogicAuthority &&
        certificate.warehouseOrganisationId !== twinLogicAuthority.warehouseOrganisationId) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["warehouseOrganisationId"],
            message: "Twin Logic certificates must bind the exact admitted warehouse organisation.",
        });
    }
    if (Boolean(certificate.compactFactEnvelope) !==
        Boolean(certificate.compactFactEnvelopeSha256)) {
        context.addIssue({ code: z.ZodIssueCode.custom, path: ["compactFactEnvelopeSha256"],
            message: "Compact fact envelope and hash must be provided together." });
    }
    if (certificate.artifactClosure && !certificate.artifactClosure.complete) {
        context.addIssue({ code: z.ZodIssueCode.custom, path: ["artifactClosure", "complete"],
            message: "A governed artifact certificate may only bind a complete export." });
    }
    if (certificate.canonicalizationVersion === "mlx.csv-artifact-bytes.v1" &&
        !certificate.artifactClosure) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["canonicalizationVersion"],
            message: "CSV byte canonicalization is valid only for a closed CSV artifact.",
        });
    }
    if (certificate.coverageAuthority &&
        (!certificate.observedAt || !certificate.freshnessPolicy)) {
        context.addIssue({ code: z.ZodIssueCode.custom, path: ["observedAt"],
            message: "Governed session-contract evidence requires an observation time and freshness policy." });
    }
}
export const twinExecutionCertificateDraftV0Schema = z
    .object(certificateClosureShape)
    .strict()
    .superRefine(refineCertificateClosure);
export const twinExecutionCertificateV0Schema = z.object({
    certificateId: z.string().trim().min(1).max(200),
    attemptNumber: z.number().int().positive(),
    invocationOrdinal: z.number().int().min(0),
    ...certificateClosureShape,
}).strict().superRefine(refineCertificateClosure);
export function twinExecutionCertificateV0Json(input) {
    const value = twinExecutionCertificateV0Schema.parse(input);
    return JSON.stringify({
        schemaVersion: value.schemaVersion,
        certificateId: value.certificateId,
        tenantSlug: value.tenantSlug,
        organisationId: value.organisationId,
        runId: value.runId,
        attemptNumber: value.attemptNumber,
        invocationOrdinal: value.invocationOrdinal,
        toolCallId: value.toolCallId,
        sqlSha256: value.sqlSha256,
        queriedTables: [...value.queriedTables].sort(),
        engineVersion: value.engineVersion,
        organisationScope: value.organisationScope,
        ...(value.warehouseOrganisationId
            ? { warehouseOrganisationId: value.warehouseOrganisationId }
            : {}),
        canonicalizationVersion: value.canonicalizationVersion,
        canonicalResultSha256: value.canonicalResultSha256,
        observedCutId: value.observedCutId,
        ...(value.observedAt ? { observedAt: value.observedAt } : {}),
        ...(value.freshnessPolicy ? { freshnessPolicy: value.freshnessPolicy } : {}),
        ...(value.queryPlanSha256 ? { queryPlanSha256: value.queryPlanSha256 } : {}),
        ...(value.coverageAuthority ? { coverageAuthority: value.coverageAuthority } : {}),
        ...(value.productAuthority ? { productAuthority: value.productAuthority } : {}),
        ...(value.joinedProductAuthority ? { joinedProductAuthority: value.joinedProductAuthority } : {}),
        ...(value.artifactClosure ? { artifactClosure: value.artifactClosure } : {}),
        ...(value.compactFactEnvelope ? { compactFactEnvelope: value.compactFactEnvelope } : {}),
        ...(value.compactFactEnvelopeSha256
            ? { compactFactEnvelopeSha256: value.compactFactEnvelopeSha256 }
            : {}),
    });
}
export const twinExecutionCertificateRefSchema = z.object({
    certificateId: z.string().trim().min(1).max(200),
    invocationOrdinal: z.number().int().min(0),
    certificateSha256: sha256Schema,
    coverageItemId: z.string().trim().min(1).max(160).optional(),
    publicationVersionId: z.string().trim().min(1).max(200).optional(),
    relation: z.string().trim().min(1).max(260).optional(),
    compactFactEnvelopeSha256: sha256Schema.optional(),
    artifactId: z.string().trim().min(1).max(200).optional(),
    artifactVersionId: z.string().trim().min(1).max(200).optional(),
    artifactSha256: sha256Schema.optional(),
    observedAt: z.number().int().positive().optional(),
    freshnessPolicy: twinExecutionFreshnessPolicySchema.optional(),
}).strict();
export function twinExecutionCertificateRef(input) {
    const { certificate } = input;
    return twinExecutionCertificateRefSchema.parse({
        certificateId: certificate.certificateId,
        invocationOrdinal: certificate.invocationOrdinal,
        certificateSha256: input.certificateSha256,
        ...(certificate.coverageAuthority ? {
            coverageItemId: certificate.coverageAuthority.coverageItemId,
            publicationVersionId: certificate.coverageAuthority.publicationVersionId,
            relation: certificate.coverageAuthority.relation,
        } : certificate.productAuthority ? {
            publicationVersionId: certificate.productAuthority.publicationVersionId,
            relation: certificate.productAuthority.relation,
        } : {}),
        ...(certificate.compactFactEnvelopeSha256
            ? { compactFactEnvelopeSha256: certificate.compactFactEnvelopeSha256 }
            : {}),
        ...(certificate.artifactClosure ? {
            artifactId: certificate.artifactClosure.artifactId,
            artifactVersionId: certificate.artifactClosure.versionId,
            artifactSha256: certificate.artifactClosure.contentSha256,
        } : {}),
        ...(certificate.observedAt ? { observedAt: certificate.observedAt } : {}),
        ...(certificate.freshnessPolicy
            ? { freshnessPolicy: certificate.freshnessPolicy }
            : {}),
    });
}
export const twinExecutionCertificateRefsSchema = z
    .array(twinExecutionCertificateRefSchema)
    .min(1)
    .max(256)
    .superRefine((refs, context) => {
    const certificateIds = new Set();
    const invocationOrdinals = new Set();
    for (const [index, ref] of refs.entries()) {
        if (certificateIds.has(ref.certificateId)) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                path: [index, "certificateId"],
                message: "Certificate references must be unique.",
            });
        }
        if (invocationOrdinals.has(ref.invocationOrdinal)) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                path: [index, "invocationOrdinal"],
                message: "Invocation ordinals must be unique within an attempt.",
            });
        }
        certificateIds.add(ref.certificateId);
        invocationOrdinals.add(ref.invocationOrdinal);
    }
});
export const twinInvocationExecutionModeSchema = z.enum([
    "governed_product",
    "exploratory_sql",
    "application_tool",
]);
export const TWIN_INVOCATION_EXECUTION_RECEIPT_SCHEMA_VERSION = "mlx.invocation-execution-receipt.v1";
/**
 * Server-stamped receipt for one successful V2 Hybrid data invocation.
 * The worker may report tool output, but it may never choose this mode. The
 * control plane validates the invoked tool against the pinned catalog (or the
 * exact governed certificate) before persisting this receipt.
 */
export const twinInvocationExecutionReceiptV1Schema = z.object({
    schemaVersion: z.literal(TWIN_INVOCATION_EXECUTION_RECEIPT_SCHEMA_VERSION),
    attemptNumber: z.number().int().positive(),
    toolCallId: z.string().trim().min(1).max(200),
    toolName: z.string().trim().min(1).max(200),
    mode: twinInvocationExecutionModeSchema,
    catalogHash: z.string().regex(/^sha256:[a-f0-9]{64}$/u),
    pluginSlug: z.string().trim().min(1).max(160).optional(),
    certificateRef: twinExecutionCertificateRefSchema.optional(),
}).strict().superRefine((receipt, context) => {
    if (receipt.mode === "governed_product" && !receipt.certificateRef) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["certificateRef"],
            message: "Governed product execution requires an exact certificate reference.",
        });
    }
    if (receipt.mode === "application_tool" && receipt.certificateRef) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["certificateRef"],
            message: "Application tool execution may not carry a warehouse certificate reference.",
        });
    }
});
